import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useContent } from "../hooks/useContent";
import { useLang } from "../context/LanguageContext";
import { SmartImage } from "../components/SmartImage";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { TurkeyMap } from "../components/TurkeyMap";
import { Reveal, Stagger, StaggerItem } from "../components/motion/Reveal";
import { CTASection } from "../components/home/FinancialBenefits";

export default function Regions() {
  const { t, tc } = useLang();
  const { data: regions } = useContent("regions");
  const { data: projects } = useContent("projects");

  return (
    <>
      <SEO pageKey="regions" title={t.seo.regions.title} description={t.seo.regions.description} />
      <PageHeader pageKey="regions"
        overline={t.regions.overline}
        title={t.regions.title}
        sub={t.regions.sub}
        image="https://images.unsplash.com/photo-1533105079780-92b9be482077?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="bg-lightgray rounded-3xl p-6 sm:p-12">
              <TurkeyMap regions={regions} projects={projects} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Stagger className="grid grid-cols-2 lg:grid-cols-3 gap-0">
            {regions.map((r) => (
              <StaggerItem key={r.slug}>
                <Link
                  to={`/regions/${r.slug}`}
                  data-testid={`region-card-${r.slug}`}
                  className="group block h-full overflow-hidden bg-white border border-border hover:shadow-2xl transition-shadow duration-500"
                >
                  <div className="aspect-[16/10] overflow-hidden hover-zoom">
                    <SmartImage src={r.image} alt={tc(r.name)} fit="cover" className="w-full h-full" />
                  </div>
                  <div className="p-3.5 sm:p-7">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display text-base sm:text-2xl text-charcoal group-hover:text-green-light transition-colors leading-tight">{tc(r.name)}</h3>
                      <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gold shrink-0" />
                    </div>
                    <p className="text-charcoal/50 text-[11px] sm:text-sm mt-1">{tc(r.tagline)}</p>
                    <ul className="mt-3 sm:mt-5 space-y-1.5 sm:space-y-2">
                      {(tc(r.highlights) || []).slice(0, 3).map((h, i) => (
                        <li key={i} className="flex items-start gap-1.5 sm:gap-2 text-charcoal/60 text-[11px] sm:text-sm"><Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-light mt-0.5 shrink-0" /> {h}</li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <CTASection />
    </>
  );
}
